import { ParseSpec } from 'prosemirror-markdown';
import { MarkSpec, NodeSpec } from 'prosemirror-model';
import { marks as basicMarks, nodes as basicNodes } from 'prosemirror-schema-basic';
import { bulletList, listItem, orderedList } from 'prosemirror-schema-list';

import type BaseExtension from '../../src/BaseExtension';
import keymap from './keymap';
import { extensionType, PerformanceType } from './types';

const { image, ...otherNodes } = basicNodes;
const { code, ...otherMarks } = basicMarks;

function getSchema<T extends BaseExtension>(extensions: { new (): T }[]) {
  const nodes: { [key: string]: NodeSpec } = { ...otherNodes };
  const marks: { [key: string]: MarkSpec } = { ...otherMarks };
  const plugins: BaseExtension['plugins'][] = [];
  const inputRules: BaseExtension['inputRules'][] = [];
  const nodePerformanceMap = new Map<string, PerformanceType>();
  let markdownTokens: Record<string, ParseSpec> = {};

  extensions.forEach((Extension) => {
    const extension = new Extension();

    // if custom schema is defined by extension, add to nodes with type for node.
    if (extension.schema) {
      switch (extension.type) {
        case extensionType.node:
          nodes[extension.name] = {
            ...(extension.schema as NodeSpec),
            attrs: {
              ...extension.schema.attrs,
              id: { default: null },
              duration: { default: null },
            },
          };
          const md = extension.parseMarkdown();
          if (md) {
            markdownTokens[extension.markdownToken ?? extension.name] = md;
          }
          nodePerformanceMap.set(extension.name, extension.performanceType);
          break;
        case extensionType.mark:
          const mdMark = extension.parseMarkdown();
          if (mdMark) {
            markdownTokens[extension.markdownToken ?? extension.name] = mdMark;
          }
          marks[extension.name] = extension.schema as MarkSpec;
      }
    }

    if (extension.keys) {
      plugins.push(keymap(extension.keys) as unknown as BaseExtension['plugins']);
    }

    if (extension.inputRules) {
      inputRules.push(extension.inputRules);
    }

    plugins.push(extension.plugins);
  }, {});

  return {
    nodes,
    marks,
    inputRules,
    plugins: plugins.filter((v) => v),
    nodePerformanceMap,
    markdownTokens,
  };
}

export default getSchema;
